/**
 * TTG Funnel Analytics - GA4 Only Version
 * Purchase journey tracking using Google Analytics 4
 * Events: view_item, add_to_cart, begin_checkout, purchase
 * @author TTG Development Team
 * @date 2025-12-23
 */

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void
    dataLayer?: unknown[]
  }
}

interface ProductData {
  product_id: number
  product_name?: string
  depart_date?: string
  quantity?: number
  value?: number
  currency?: string
}

interface PurchaseData extends ProductData {
  order_id: number
  transaction_id?: string
}

/**
 * Safely call gtag with fallback to dataLayer
 */
function sendGA4Event(eventName: string, params: Record<string, unknown>): void {
  if (typeof window.gtag === 'function') {
    window.gtag('event', eventName, params)
  } else if (Array.isArray(window.dataLayer)) {
    window.dataLayer.push({ event: eventName, ...params })
  }
  
  // Debug log in development
  if (import.meta.env.DEV) {
    console.log('[GA4 Event]', eventName, params)
  }
}

/**
 * Track view_item - when user views a product detail page
 */
export function trackViewItem(product: ProductData): void {
  sendGA4Event('view_item', {
    currency: product.currency || 'USD',
    value: product.value || 0,
    items: [{
      item_id: String(product.product_id),
      item_name: product.product_name || '',
      price: product.value || 0,
      quantity: 1
    }]
  })
}

/**
 * Track add_to_cart - when user clicks book/continue button
 */
export function trackAddToCart(product: ProductData): void {
  sendGA4Event('add_to_cart', {
    currency: product.currency || 'USD',
    value: product.value || 0,
    items: [{
      item_id: String(product.product_id),
      item_name: product.product_name || '',
      price: product.value || 0,
      quantity: product.quantity || 1
    }]
  })
}

/**
 * Track begin_checkout - when user enters payment page
 */
export function trackBeginCheckout(value?: number, currency?: string): void {
  sendGA4Event('begin_checkout', {
    currency: currency || 'USD',
    value: value || 0
  })
}

/**
 * Track purchase - when payment is completed
 */
export function trackPurchase(data: PurchaseData): void {
  sendGA4Event('purchase', {
    transaction_id: data.transaction_id || String(data.order_id),
    currency: data.currency || 'USD',
    value: data.value || 0,
    items: [{
      item_id: String(data.product_id),
      item_name: data.product_name || '',
      price: data.value || 0,
      quantity: data.quantity || 1
    }]
  })
}

/**
 * Track session start (optional - GA4 tracks this automatically)
 */
export function trackSessionStart(): void {
  // GA4 automatically tracks session_start, but we can send a custom event if needed
  sendGA4Event('session_start', {
    page_location: window.location.href,
    page_title: document.title
  })
}
